import React, { useState } from 'react';
import './staffManage.css';

const UpdatePractitioner = ({ practitioners, onUpdatePractitioner }) => {
  const [selectedDiscipline, setSelectedDiscipline] = useState('');
  const [selectedId, setSelectedId] = useState('');
  const [selectedFirstName, setSelectedFirstName] = useState('');
  const [selectedLastName, setSelectedLastName] = useState('');
  const [firstName, setFirstName] = useState('');
  const [middleName, setMiddleName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [discipline, setDiscipline] = useState('');
  const [licenseNumber, setLicenseNumber] = useState('');
  const [licenseExpiry, setLicenseExpiry] = useState('');
  const [specialization, setSpecialization] = useState('');
  const [yearsOfExperience, setYearsOfExperience] = useState('');
  const [verificationStatus, setVerificationStatus] = useState('');
  const [errors, setErrors] = useState({});

  // Handle Discipline Change and reset filters
  const handleDisciplineChange = (e) => {
    setSelectedDiscipline(e.target.value);
    resetFilters();
  };

  // Reset filters and update fields
  const resetFilters = () => {
    setSelectedId('');
    setSelectedFirstName('');
    setSelectedLastName('');
    resetUpdateFields();
  };

  // Reset update form fields
  const resetUpdateFields = () => {
    setFirstName('');
    setMiddleName('');
    setLastName('');
    setEmail('');
    setPhone('');
    setDiscipline('');
    setLicenseNumber('');
    setLicenseExpiry('');
    setSpecialization('');
    setYearsOfExperience('');
    setVerificationStatus('');
  };

  // Unique values for filtering dropdowns
  const uniqueDisciplines = [...new Set(practitioners.map((p) => p.discipline))];
  const uniqueIds = [...new Set(practitioners
    .filter((p) => selectedDiscipline === '' || p.discipline === selectedDiscipline)
    .map((p) => p.id.toString()))];
  const uniqueFirstNames = [...new Set(practitioners
    .filter((p) => selectedDiscipline === '' || p.discipline === selectedDiscipline)
    .map((p) => p.firstName))];
  const uniqueLastNames = [...new Set(practitioners
    .filter((p) => selectedDiscipline === '' || p.discipline === selectedDiscipline)
    .map((p) => p.lastName))];

  // Populate fields by selecting Practitioner ID
  const handleSelectChange = (e) => {
    const id = e.target.value;
    setSelectedId(id);
    populateFieldsById(id);
  };

  // Populate fields by selecting Practitioner Name
  const handleNameChange = () => {
    const practitioner = practitioners.find(p =>
      p.firstName === selectedFirstName &&
      p.lastName === selectedLastName &&
      (selectedDiscipline === '' || p.discipline === selectedDiscipline)
    );

    if (practitioner) {
      setSelectedId(practitioner.id.toString());
      populateFields(practitioner);
    }
  };

  // Populate fields based on Practitioner ID
  const populateFieldsById = (id) => {
    const practitioner = practitioners.find((p) => p.id.toString() === id);
    if (practitioner) {
      populateFields(practitioner);
    }
  };

  // Populate form fields based on Practitioner object
  const populateFields = (practitioner) => {
    setFirstName(practitioner.firstName);
    setMiddleName(practitioner.middleName);
    setLastName(practitioner.lastName);
    setEmail(practitioner.email);
    setPhone(practitioner.phone);
    setDiscipline(practitioner.discipline);
    setLicenseNumber(practitioner.licenseNumber);
    setLicenseExpiry(practitioner.licenseExpiry);
    setSpecialization(practitioner.specialization);
    setYearsOfExperience(practitioner.yearsOfExperience);
    setVerificationStatus(practitioner.verificationStatus);
  };

  // Handle form submission and validation
  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validate()) return;

    const updatedPractitioner = {
      id: selectedId,
      firstName,
      middleName,
      lastName,
      email,
      phone,
      discipline,
      licenseNumber,
      licenseExpiry,
      specialization,
      yearsOfExperience,
      verificationStatus
    };
    onUpdatePractitioner(updatedPractitioner);
    resetFilters();
    setErrors({});
  };

  // Validate form input
  const validate = () => {
    let valid = true;
    const newErrors = {};

    if (!selectedId) {
      newErrors.selectedId = "Please select a practitioner by ID";
      valid = false;
    }
    if (!firstName.trim()) {
      newErrors.firstName = "First name is required";
      valid = false;
    }
    if (!lastName.trim()) {
      newErrors.lastName = "Last name is required";
      valid = false;
    }
    if (!email.trim() || !/\S+@\S+\.\S+/.test(email)) {
      newErrors.email = "Valid email is required";
      valid = false;
    }
    if (!phone.trim() || !/^\d{10}$/.test(phone)) {
      newErrors.phone = "Valid 10-digit phone number is required";
      valid = false;
    }
    if (!licenseNumber.trim()) {
      newErrors.licenseNumber = "License number is required";
      valid = false;
    }
    if (!licenseExpiry.trim() || new Date(licenseExpiry) < new Date()) {
      newErrors.licenseExpiry = "Valid license expiry date is required";
      valid = false;
    }
    if (!specialization.trim()) {
      newErrors.specialization = "Specialization is required";
      valid = false;
    }
    if (!yearsOfExperience.trim() || isNaN(yearsOfExperience)) {
      newErrors.yearsOfExperience = "Valid years of experience is required";
      valid = false;
    }

    setErrors(newErrors);
    return valid;
  };

  return (
    <form onSubmit={handleSubmit}>
      <h3>Update Practitioner</h3>

      <div className="filters-section">
        <h4>Filters</h4>
        <select value={selectedDiscipline} onChange={handleDisciplineChange}>
          <option value="">All Disciplines</option>
          {uniqueDisciplines.map((discipline, index) => (
            <option key={index} value={discipline}>
              {discipline}
            </option>
          ))}
        </select>

        <select value={selectedId} onChange={handleSelectChange} disabled={!selectedDiscipline}>
          <option value="" disabled>Select Practitioner by ID</option>
          {uniqueIds.map((id, index) => (
            <option key={index} value={id}>
              {id}
            </option>
          ))}
        </select>

        <div className="name-fields">
          <select value={selectedFirstName} onChange={(e) => setSelectedFirstName(e.target.value)} disabled={!selectedDiscipline}>
            <option value="">All First Names</option>
            {uniqueFirstNames.map((firstName, index) => (
              <option key={index} value={firstName}>
                {firstName}
              </option>
            ))}
          </select>

          <select value={selectedLastName} onChange={(e) => setSelectedLastName(e.target.value)} disabled={!selectedDiscipline}>
            <option value="">All Last Names</option>
            {uniqueLastNames.map((lastName, index) => (
              <option key={index} value={lastName}>
                {lastName}
              </option>
            ))}
          </select>
        </div>
        <button type="button" className="secondary-btn" onClick={handleNameChange} disabled={!selectedFirstName || !selectedLastName}>
          Load Practitioner by Name
        </button>
      </div>

      <div className="updating-fields">
        <h4>Update Fields</h4>
        <div className="name-fields">
          <input 
            type="text" 
            value={firstName} 
            onChange={(e) => setFirstName(e.target.value)} 
            placeholder="First Name" 
            required 
          />
          <input 
            type="text" 
            value={middleName} 
            onChange={(e) => setMiddleName(e.target.value)} 
            placeholder="Middle Name" 
          />
          <input 
            type="text" 
            value={lastName} 
            onChange={(e) => setLastName(e.target.value)} 
            placeholder="Last Name" 
            required 
          />
        </div>
        {errors.firstName && <p className="error">{errors.firstName}</p>}
        {errors.lastName && <p className="error">{errors.lastName}</p>}

        <input 
          type="email" 
          value={email} 
          onChange={(e) => setEmail(e.target.value)} 
          placeholder="Practitioner Email" 
          required 
        />
        {errors.email && <p className="error">{errors.email}</p>}

        <input 
          type="text" 
          value={phone} 
          onChange={(e) => setPhone(e.target.value)} 
          placeholder="Practitioner Phone Number" 
          required 
        />
        {errors.phone && <p className="error">{errors.phone}</p>}

        <select value={discipline} onChange={(e) => setDiscipline(e.target.value)}>
          <option value="Physiotherapy">Physiotherapy</option>
          <option value="Massage Therapy">Massage Therapy</option>
          <option value="Chiropractic">Chiropractic</option>
          <option value="Orthopaedics">Orthopaedics</option>
        </select>

        <input 
          type="text" 
          value={licenseNumber} 
          onChange={(e) => setLicenseNumber(e.target.value)} 
          placeholder="License Number" 
          required 
        />
        {errors.licenseNumber && <p className="error">{errors.licenseNumber}</p>}

        <input 
          type="date" 
          value={licenseExpiry} 
          onChange={(e) => setLicenseExpiry(e.target.value)} 
          required 
        />
        {errors.licenseExpiry && <p className="error">{errors.licenseExpiry}</p>}

        <input 
          type="text" 
          value={specialization} 
          onChange={(e) => setSpecialization(e.target.value)} 
          placeholder="Specialization" 
          required 
        />
        {errors.specialization && <p className="error">{errors.specialization}</p>}

        <input 
          type="number" 
          value={yearsOfExperience} 
          onChange={(e) => setYearsOfExperience(e.target.value)} 
          placeholder="Years of Experience" 
          required 
          min="0"
        />
        {errors.yearsOfExperience && <p className="error">{errors.yearsOfExperience}</p>}

        <select value={verificationStatus} onChange={(e) => setVerificationStatus(e.target.value)} required>
          <option value="Pending">Pending</option>
          <option value="Verified">Verified</option>
          <option value="Rejected">Rejected</option>
        </select>
      </div>

      <button type="submit" className='add-practitioner-btn'>Update Practitioner</button>
    </form>
  );
};

export default UpdatePractitioner;
